// src/datadog.ts
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogLogs } from '@datadog/browser-logs';
import { DATADOG_CLIENT_TOKEN, DATADOG_RUM_APPLICATION_ID, DATADOG_RUM_CLIENT_TOKEN, SEND_DD_LOGS_DEV } from '../env';
import { getTenantKey, getTenantId, getDatadogEnv } from './utils';

const isLocal = (process.env.REACT_APP_STAGE || 'local') === 'local';
const datadogEnabled = !isLocal || SEND_DD_LOGS_DEV;

if (datadogEnabled) {
  // --- RUM Setup ---
  datadogRum.init({
    applicationId: DATADOG_RUM_APPLICATION_ID,
    clientToken: DATADOG_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'praxis-pro-frontend',
    env: getDatadogEnv(),
    version: process.env.REACT_APP_VERSION || '0.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin({ router: false })],
  });

  datadogRum.setGlobalContextProperty('tenant_id', getTenantId());
  datadogRum.setGlobalContextProperty('tenant_key', getTenantKey());

  datadogRum.startSessionReplayRecording();

  // --- Logs Setup ---
  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'praxis-pro-frontend',
    env: getDatadogEnv(),
    version: process.env.REACT_APP_VERSION || '0.0.0',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  });

  datadogLogs.setGlobalContextProperty('tenant_id', getTenantId());
  datadogLogs.setGlobalContextProperty('tenant_key', getTenantKey());
}

// Bumped on every logout so an identify() requested before logout but resolved
// (e.g. a fetchUser().then() still in flight) after it is not applied — the SPA
// never reloads on logout, so a stale call would otherwise reattach usr.id for
// the rest of the session.
let identityEpoch = 0;

export function getDatadogIdentityEpoch(): number {
  return identityEpoch;
}

// Identifies the current user to RUM and Logs so every entry sent by the SDK —
// including RUM sessions/errors and logs forwarded from raw console calls —
// carries usr.id, not just logs routed through the custom logger's enrichers.
// Pass the epoch captured via getDatadogIdentityEpoch() at the time the user
// lookup was started, so a resolution racing behind a logout is dropped.
export function identifyDatadogUser(id?: string, epoch?: number): void {
  if (!datadogEnabled || !id) return;
  if (epoch !== undefined && epoch !== identityEpoch) return;
  datadogRum.setUser({ id });
  datadogLogs.setUser({ id });
}

export function clearDatadogUser(): void {
  identityEpoch += 1;
  if (!datadogEnabled) return;
  datadogRum.clearUser();
  datadogLogs.clearUser();
}

export {
    datadogRum,
    datadogLogs,
};
